import { useState, useEffect, useMemo } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { CACHE_VERSION } from "../lib/cacheVersion";
import { toOrgDateKey } from "../lib/timezone";
import { formatDateShort, formatCompact } from "../utils/formatters";
import {
  pickChartGranularity,
  aggregateDailySeries,
  type ChartGranularity,
  type DailyPoint,
} from "../utils/chartGranularity";

// =============================================================================
// useDailyOrgMetrics — canonical daily-rollup primitive (TE-F2)
// Reads daily_org_metrics view. Replaces the ad-hoc useEffect in
// ClientPortalPage.tsx (lines 92-138). Cache pattern mirrors useOperationsKpis.
// =============================================================================

export interface ChartDataPoint {
  date: string; // formatted M/D/YYYY via formatDateShort
  cash: number;
  revenue: number;
  label?: string; // formatCompact(cash) when cash > 0, else ""
}

// --- Cache Configuration ---
const HISTORICAL_TTL = 24 * 60 * 60 * 1000; // 24h — historical ranges don't change
const LIVE_TTL = 10 * 60 * 1000; // 10min — today's data changes frequently

interface CacheEntry {
  data: ChartDataPoint[];
  timestamp: number;
}

interface DateRange {
  start: Date | null;
  end: Date | null;
}

function getCacheTTL(endDateStr: string): number {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(23, 59, 59, 999);
  const endDate = new Date(endDateStr + "T12:00:00");
  return endDate < yesterday ? HISTORICAL_TTL : LIVE_TTL;
}

function readCache(key: string, endDateStr: string): ChartDataPoint[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    const ttl = getCacheTTL(endDateStr);
    if (Date.now() - entry.timestamp > ttl) {
      localStorage.removeItem(key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: ChartDataPoint[]): void {
  try {
    const entry: CacheEntry = { data, timestamp: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Quota exceeded — non-fatal
  }
}

// =============================================================================
// useDailyOrgMetrics
// =============================================================================
export function useDailyOrgMetrics(
  orgId: string | null,
  dateRange: DateRange
): {
  data: ChartDataPoint[];
  isLoading: boolean;
  error: Error | null;
  granularity: ChartGranularity;
} {
  // Stable YYYY-MM-DD strings — prevent infinite re-render from Date identity churn
  const startDateStr = useMemo(
    () => (dateRange.start ? toOrgDateKey(dateRange.start) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange.start?.getTime()]
  );
  const endDateStr = useMemo(
    () => (dateRange.end ? toOrgDateKey(dateRange.end) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange.end?.getTime()]
  );

  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Edge case: missing required params — return empty, no query
    if (!orgId || !dateRange.start || !dateRange.end || !startDateStr || !endDateStr) {
      setData([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const cacheKey = `altara_${CACHE_VERSION}_daily_metrics_${orgId}_${startDateStr}_${endDateStr}`;

    // Stale-while-revalidate: show cached data immediately, refresh in background
    const cached = readCache(cacheKey, endDateStr);
    if (cached) {
      setData(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    const fetchDaily = async () => {
      if (!supabase) return;

      try {
        const { data: rows, error: queryError } = await supabase
          .from("daily_org_metrics")
          .select("date, cash_collected, revenue_closed")
          .eq("organization_id", orgId)
          .gte("date", startDateStr)
          .lte("date", endDateStr)
          .order("date");

        if (cancelled) return;
        if (queryError) throw queryError;

        const points: ChartDataPoint[] = (rows ?? []).map((d) => {
          const cash = Number(d.cash_collected) || 0;
          const revenue = Number(d.revenue_closed) || 0;
          return {
            // T12:00:00 anchor avoids timezone-rollover bugs (CLAUDE.md gotcha #16)
            date: formatDateShort(new Date(d.date + "T12:00:00")),
            cash,
            revenue,
            label: cash > 0 ? formatCompact(cash) : "",
          };
        });

        if (!cancelled) {
          setData(points);
          setError(null);
          writeCache(cacheKey, points);
        }
      } catch (e) {
        if (!cancelled) {
          const err = e instanceof Error ? e : new Error(String(e));
          logger.error("useDailyOrgMetrics failed:", e);
          setError(err);
          // Leave data at last-known-good (either cached or [] from init)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchDaily();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, startDateStr, endDateStr]);

  // Adaptive granularity for display: keep `data` (and its cache) as raw DAILY points,
  // but roll up to weekly/monthly over long ranges so the chart stays readable. Daily is
  // short-circuited so short ranges are byte-identical to the pre-rollup behaviour. The
  // `date` string is "M/D/YYYY" (formatDateShort, year included) so parsing it back to a
  // real date is reliable. NOTE blast radius: also feeds ClientPortalPage.
  const { chartData, granularity } = useMemo(() => {
    const spanDays =
      dateRange.start && dateRange.end
        ? Math.round((dateRange.end.getTime() - dateRange.start.getTime()) / 86_400_000) + 1
        : data.length;
    const g = pickChartGranularity(spanDays);
    if (g === "daily") return { chartData: data, granularity: g };

    const dailyPoints: DailyPoint[] = data.map((p) => {
      const [m, d, y] = p.date.split("/").map(Number);
      return { ts: new Date(y, m - 1, d, 12).getTime(), cash: p.cash, revenue: p.revenue };
    });
    return { chartData: aggregateDailySeries(dailyPoints, g), granularity: g };
  }, [data, dateRange.start, dateRange.end]);

  return { data: chartData, granularity, isLoading, error };
}
